import { Injectable } from '@angular/core';
import { ApiHttpClient, ListOption, ListOptionRequest } from '../modules/tcm-core.module';
import { InsurancePlanClaimUnbundlingRuleUpdateRequest, InsurancePlanClaimUnbundlingRuleViewModel, InsurancePlanCopyRequest, InsurancePlanListOptionByClinicRequest, InsurancePlanListOptionByContractsRequest, InsurancePlanListOptionWithExceptRequest, InsurancePlanListOptionForClaimUnbundlingRuleRequest,InsurancePlanDispensingPriceModeUpdateRequest, InsurancePlanProductModifierModel, InsurancePlanProductModifierCopyViewModel } from '../models/insurance-plans';
import { InsurancePlanListOptionByClinicAndIdRequest } from '../models/insurance-plans/insurance-plan-list-option-by-clinic-and-id-request';
import { InsurancePlanFilingMethodEnum, DispensingPlanModeEnum } from '../models/enums';
import { InsurancePlanListOptionByFilingMethodRequest } from '../models/insurance-plans/insurance-plan-list-option-by-filing-method-request';
import { InsurancePlanProductModifierAddRequest } from '../models/insurance-plans/insurance-plan-product-modifier-add-request';
import { InsurancePlanProductModifierDeleteRequest } from '../models/insurance-plans/insurance-plan-product-modifier-delete-request';
import { InsurancePlanProductModifierValidationInfoResponse } from '../models/insurance-plans/insurance-plan-product-modifier-validation-info-response';
import { ProviderEnrollmentInsurancePlanViewModel } from '../models/provider-enrollment-insurance-plan/provider-enrollment-insurance-plan/provider-enrollment-insurance-plan-view-model';
import { FileResponse } from '../modules/tcm-core.module/models/files/file-response';

@Injectable({
  providedIn: 'root'
})
export class InsurancePlanDataService {
  constructor(private readonly httpClient: ApiHttpClient) {
  }

  getInsurancePlanOptionsAsync(query: string, cancellationToken: Promise<any>): Promise<ListOption<number>[]> {
    const request: ListOptionRequest = { searchString: query };
    return this.httpClient.getJsonAsync<ListOption<number>[]>('InsurancePlans/options', request, cancellationToken);
  }

  getSecInsurancePlanOptionsAsync(query: string, cancellationToken: Promise<any>): Promise<ListOption<number>[]> {
    const request: ListOptionRequest = { searchString: query };
    return this.httpClient.getJsonAsync<ListOption<number>[]>('InsurancePlans/optionsSecondaryInsurance', request, cancellationToken);
  }

  getInsurancePlanByFilingMethodOptionsAsync(query: string, filingMethod: InsurancePlanFilingMethodEnum, cancellationToken: Promise<any>): Promise<ListOption<number>[]> {
    const request: InsurancePlanListOptionByFilingMethodRequest = { searchString: query, filingMethod: filingMethod };
    return this.httpClient.getJsonAsync<ListOption<number>[]>('insurancePlans/optionsByFilingMethod', request, cancellationToken);
  }

  getInsurancePlanOptionsByContractAsync(query: string, contractIds: number[], cancellationToken: Promise<any>): Promise<ListOption<number>[]> {
    const request: InsurancePlanListOptionByContractsRequest = { searchString: query, contractIds: contractIds };
    return this.httpClient.getJsonAsync<ListOption<number>[]>('InsurancePlans/ListOptionsByContracts', request, cancellationToken);
  }

  getInsurancePlanOptionsWithExceptAsync(query: string, exceptId: number, cancellationToken: Promise<any>): Promise<ListOption<number>[]> {
    let request = {} as InsurancePlanListOptionWithExceptRequest;
    request.searchString = query;
    request.exceptId = exceptId;
    return this.httpClient.getJsonAsync<ListOption<number>[]>('InsurancePlans/ListOptionsWithExcept', request, cancellationToken);
  }

  getInsurancePlanNotPrivateOptionsAsync(query: string, cancellationToken: Promise<any>): Promise<ListOption<number>[]> {
    const request: ListOptionRequest = { searchString: query };
    return this.httpClient.getJsonAsync<ListOption<number>[]>('InsurancePlans/NotPrivateListOptions', request, cancellationToken);
  }

  getInsurancePlanWithShortInfoAsync(query: string, clinicId: number, cancellationToken: Promise<any>): Promise<ListOption<number>[]> {
    const request: InsurancePlanListOptionByClinicRequest = { searchString: query, clinicId: clinicId };
    return this.httpClient.getJsonAsync<ListOption<number>[]>('InsurancePlans/ListOptionsWithShortInfo', request, cancellationToken);
  }

  getInsurancePlanWithShortInfoAndSelectedPlanAsync(query: string, clinicId: number, insurancePlanId: number, fromPriceCheck: boolean, requestInsurancePlanId, cancellationToken: Promise<any>): Promise<ListOption<number>[]> {
    const request: InsurancePlanListOptionByClinicAndIdRequest = { searchString: query, clinicId: clinicId, id: insurancePlanId, fromPriceCheck: fromPriceCheck, insurancePlanId: requestInsurancePlanId };
    return this.httpClient.getJsonAsync<ListOption<number>[]>('InsurancePlans/ListOptionsWithShortInfoAndSelectedPlan', request, cancellationToken);
  }

  getInsurancePlanForClaimUnbundlingRuleOptionsAsync(query: string, claimUnbundlingRuleId: number, cancellationToken: Promise<any>): Promise<ListOption<number>[]> {
    const request: InsurancePlanListOptionForClaimUnbundlingRuleRequest = { searchString: query, claimUnbundlingRuleId: claimUnbundlingRuleId };
    return this.httpClient.getJsonAsync<ListOption<number>[]>('InsurancePlans/ListOptionsForClaimUnbundlingRule', request, cancellationToken);
  }

  getInsurancePlanClaimUnbundlingRuleAsync(id: number, cancellationToken: Promise<any>): Promise<InsurancePlanClaimUnbundlingRuleViewModel> {
    const request: any = { id: id };
    return this.httpClient.getJsonAsync<InsurancePlanClaimUnbundlingRuleViewModel>('InsurancePlans/ClaimUnbundlingRule', request, cancellationToken);
  }

  getInsurancePlanOptionsforClaimsAsync(query: string, cancellationToken: Promise<any>): Promise<ListOption<number>[]> {
    const request: ListOptionRequest = { searchString: query };
    return this.httpClient.getJsonAsync<ListOption<number>[]>('InsurancePlans/claimsOptions', request, cancellationToken);
  }

  setInsurancePlanClaimUnbundlingRuleAsync(id: number, claimUnbundlingRuleId: number, cancellationToken: Promise<any>): Promise<any> {
    const request: InsurancePlanClaimUnbundlingRuleUpdateRequest = { id: id, claimUnbundlingRuleId: claimUnbundlingRuleId };
    return this.httpClient.postJsonAsync('InsurancePlans/ClaimUnbundlingRule', request, cancellationToken, false);
  }

  copy(id: number, name: string, cancellationToken: Promise<any>): Promise<number> {
    const request: InsurancePlanCopyRequest = { id: id, name: name };
    return this.httpClient.postJsonAsync('InsurancePlans/Copy', request, cancellationToken);
  }

  async downloadAndDisplayFileAsync(id: number | string): Promise<any> {
    try {
      var fileResponse = await this.downloadFileBlobAsync(id);

      await fileResponse.displayFileAsync();
    }
    catch (e) {

    }
  }

  checkErpIdExistsAsync(erpId: any, planId?: number, timeout?: Promise<any>): Promise<boolean> {
    const request = { erpId: erpId, planId: planId };

    return this.httpClient.getJsonAsync<boolean>('InsurancePlans/CheckUserErpIdExist', request, timeout);
  }

  updateDispensingPlanPriceMode(insurancePlanId: number, dispensingPlanMode: DispensingPlanModeEnum, cancellationToken?: Promise<any>): Promise<any> {
    const request = { id: insurancePlanId, dispensingPlanMode: dispensingPlanMode } as InsurancePlanDispensingPriceModeUpdateRequest;

    return this.httpClient.postJsonAsync('InsurancePlans/UpdateDispensingPlanPriceMode', request, cancellationToken, true);
  }

  private downloadFileBlobAsync(id: number | string): Promise<FileResponse> {
    return this.httpClient.getFileAsync(`InsurancePlans/download?id=${id.toString()}`);
  }

  public addProductModifiers(request: InsurancePlanProductModifierAddRequest, timeout: Promise<any>): Promise<boolean> {
    return this.httpClient.postJsonAsync<boolean>('InsurancePlans/AddProductModifiers', request, timeout, false);
  }

  public deleteProductModifiers(request: InsurancePlanProductModifierDeleteRequest, timeout: Promise<any>): Promise<boolean> {
    return this.httpClient.postJsonAsync<boolean>('InsurancePlans/DeleteProductModifiers', request, timeout, false);
  }

  public getInsurancePlanModifiersCopyValidationInfo(request: InsurancePlanProductModifierCopyViewModel, timeout: Promise<any>): Promise<InsurancePlanProductModifierValidationInfoResponse> {
    return this.httpClient.getJsonAsync<InsurancePlanProductModifierValidationInfoResponse>('InsurancePlanCopyProductModifiers/GetValidationInfo', request, timeout, false);  
  }

  validatePolicyNumberFormat(policyNumber: string, insurancePlanId: number, timeout?: Promise<boolean>) {
    const request = {policyNumber: policyNumber, insurancePlanId: insurancePlanId};

    return this.httpClient.getJsonAsync<boolean>('InsurancePlans/ValidatePolicyNumberFormat', request, timeout);
  }

  
  deactivateProvidersAsync(insurancePlan: number, selectedIds: number[], cancellation: Promise<any>): Promise<number> {
    const request = {} as ProviderEnrollmentInsurancePlanViewModel;
    request.selectedIds = selectedIds;
    request.insurancePlanId = insurancePlan;
    return this.httpClient.postJsonAsync('ProviderEnrollmentInsurancePlan/DeactivateProviders', request, cancellation);
  }

  onAddProviderAction(providerIds: number[], insurancePlanId: number, cancellation: Promise<any>): Promise<any> {
    const request: ProviderEnrollmentInsurancePlanViewModel = {
      providerIds: providerIds,
      insurancePlanId: insurancePlanId,
      insurancePlanName: null,
      isActive: true,
      providerName: null,
      providerId: null,
      isNetworkProvider:false
    };
    return this.httpClient.postJsonAsync('ProviderEnrollmentInsurancePlan/AddProviders', request, cancellation);
  }
}
